import type {
  DraftSource,
  DraftWarning,
  DuplicateCandidate,
  DuplicateResolution,
} from '@recipes/contracts';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, cn } from '@recipes/ui';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

/** Prominent source attribution: we must never imply ownership of imported content. */
export function SourceBox({
  source,
  fallbackUrl,
}: {
  source: DraftSource | null;
  fallbackUrl?: string | null;
}) {
  const { t } = useTranslation();
  const url = source?.canonicalUrl ?? source?.url ?? fallbackUrl ?? null;
  const domain = source?.domain ?? (url ? safeHost(url) : null);
  return (
    <Card className="border-info/50 bg-info/5" data-testid="import-source-box">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('imports.source.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          {(source?.siteName || domain) && (
            <>
              <dt className="text-muted-foreground">{t('imports.source.site')}</dt>
              <dd className="font-medium">{source?.siteName ?? domain}</dd>
            </>
          )}
          {source?.author && (
            <>
              <dt className="text-muted-foreground">{t('imports.source.author')}</dt>
              <dd>{source.author}</dd>
            </>
          )}
          {source?.originalTitle && (
            <>
              <dt className="text-muted-foreground">{t('imports.source.originalTitle')}</dt>
              <dd>{source.originalTitle}</dd>
            </>
          )}
          {url && (
            <>
              <dt className="text-muted-foreground">{t('imports.source.link')}</dt>
              <dd className="break-all">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-1 text-primary underline"
                >
                  {url}
                  <ExternalLink className="size-3.5" aria-hidden />
                  <span className="sr-only">{t('imports.source.opensNewTab')}</span>
                </a>
              </dd>
            </>
          )}
          {source?.attribution && (
            <>
              <dt className="text-muted-foreground">{t('imports.source.attribution')}</dt>
              <dd>{source.attribution}</dd>
            </>
          )}
        </dl>
        <p className="pt-2 text-xs text-muted-foreground">{t('imports.source.notOurs')}</p>
      </CardContent>
    </Card>
  );
}

function safeHost(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

const SEVERITY_VARIANT = { info: 'info', warning: 'warning', critical: 'destructive' } as const;

export function WarningsList({
  warnings,
  uncertainties = [],
}: {
  warnings: DraftWarning[];
  uncertainties?: string[];
}) {
  const { t } = useTranslation();
  if (warnings.length === 0 && uncertainties.length === 0) return null;
  return (
    <section
      aria-label={t('review.warningsTitle')}
      className="space-y-2"
      data-testid="review-warnings"
    >
      {warnings.map((w, i) => (
        <Alert
          key={`${w.code}-${i}`}
          variant={SEVERITY_VARIANT[w.severity]}
          title={t(`review.warningCodes.${w.code}`)}
        >
          <p>{w.message}</p>
        </Alert>
      ))}
      {uncertainties.length > 0 && (
        <Alert variant="warning" title={t('review.uncertaintiesTitle')}>
          <ul className="list-disc space-y-1 pl-4">
            {uncertainties.map((u, i) => (
              <li key={i}>{u}</li>
            ))}
          </ul>
        </Alert>
      )}
    </section>
  );
}

export interface DuplicateChoice {
  resolution: DuplicateResolution;
  existingRecipeId: string | null;
}

export function DuplicatesPanel({
  duplicates,
  choice,
  onChange,
}: {
  duplicates: DuplicateCandidate[];
  choice: DuplicateChoice;
  onChange: (choice: DuplicateChoice) => void;
}) {
  const { t } = useTranslation();
  if (duplicates.length === 0) return null;
  return (
    <Alert variant="warning" title={t('imports.duplicates.title')} data-testid="duplicates-panel">
      <p className="mb-2">{t('imports.duplicates.description')}</p>
      <ul className="space-y-2">
        {duplicates.map((d) => {
          const selected = choice.existingRecipeId === d.recipeId;
          return (
            <li
              key={d.recipeId}
              className={cn('rounded-md border bg-background p-2', selected && 'border-primary')}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{d.title}</span>
                <Badge variant="muted">{t(`imports.duplicates.reasons.${d.reason}`)}</Badge>
                <span className="text-xs text-muted-foreground">
                  {t('imports.duplicates.similarity', { value: Math.round(d.similarity * 100) })}
                </span>
                {selected && choice.resolution !== 'SAVE_NEW' && (
                  <Badge variant="info">
                    {t(`imports.duplicates.actions.${choice.resolution}`)}
                  </Badge>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button asChild size="sm" variant="outline">
                  <Link to={`/recipes/${d.recipeId}`}>
                    {t('imports.duplicates.actions.OPEN_EXISTING')}
                  </Link>
                </Button>
                <Button
                  size="sm"
                  variant={
                    selected && choice.resolution === 'UPDATE_EXISTING' ? 'default' : 'outline'
                  }
                  aria-pressed={selected && choice.resolution === 'UPDATE_EXISTING'}
                  onClick={() =>
                    onChange({ resolution: 'UPDATE_EXISTING', existingRecipeId: d.recipeId })
                  }
                >
                  {t('imports.duplicates.actions.UPDATE_EXISTING')}
                </Button>
                <Button
                  size="sm"
                  variant={
                    selected && choice.resolution === 'SAVE_AS_VARIATION' ? 'default' : 'outline'
                  }
                  aria-pressed={selected && choice.resolution === 'SAVE_AS_VARIATION'}
                  onClick={() =>
                    onChange({ resolution: 'SAVE_AS_VARIATION', existingRecipeId: d.recipeId })
                  }
                >
                  {t('imports.duplicates.actions.SAVE_AS_VARIATION')}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-3">
        <Button
          size="sm"
          variant={choice.resolution === 'SAVE_NEW' ? 'default' : 'outline'}
          aria-pressed={choice.resolution === 'SAVE_NEW'}
          onClick={() => onChange({ resolution: 'SAVE_NEW', existingRecipeId: null })}
        >
          {t('imports.duplicates.actions.SAVE_NEW')}
        </Button>
      </div>
    </Alert>
  );
}
